"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ModelSelectorDialog,
  ModelSelectorEmpty,
  ModelSelectorGroup,
  ModelSelectorInput,
  ModelSelectorItem,
  ModelSelectorList,
  ModelSelectorName,
  ModelSelectorLogoGroup,
} from "@/components/ai-elements/model-selector";
import { Play, ImagePlus, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useModelStore, type ModelItem } from "@/client/store/modelStore";
import { findModelConfig, type VersionOption } from "@/client/config/models.config";
import { getProviderConfig } from "@/client/config/providers.config";
import { evaluateCondition, resolveUploadMaxFiles } from "./schema/helpers";
import type { QueueStats } from "@/client/hooks/useTaskQueue";
import type {
  SchemaCondition,
  SchemaDataFieldDefinition,
  SchemaFieldDefinition,
  SchemaFormDefinition,
  SchemaFormValues,
} from "./schema/types";
import { FileUpload, type UploadFileItem } from "@/client/components/FileUpload";
import { TaskProgressBar } from "./TaskProgressBar";

interface PromptDockProps {
  content?: React.ReactNode;
  onSubmit?: () => void;
  submitting?: boolean;
  queueStats?: QueueStats | null;
  queueLength?: number;
  models: ModelItem[];
  currentModel?: string;
  onModelChange?: (modelValue: string) => void;
  controllerRef?: React.MutableRefObject<{
    getSchema?: () => unknown;
    getFieldValue?: (key: string) => unknown;
    setFieldValue?: (key: string, value: unknown) => void;
    subscribeValueChanges?: (listener: () => void) => () => void;
  } | null> | null;
  versionFieldKey?: string;
  versionValue?: string;
  versionLabel?: string;
  versionOptions?: VersionOption[];
  onVersionChange?: (value: string) => void;
  schemaSignature?: string;
  /** Dock 定位方式：
   * - sticky: 跟随内容容器吸底
   * - fixed: 固定视口底部（全局 Dock）
   * - floating: 内容区内部悬浮（绝对定位，需由父容器提供 relative）
   */
  position?: 'sticky' | 'fixed' | 'floating';
}

const PromptDock: React.FC<PromptDockProps> = ({
  content,
  onSubmit,
  submitting,
  queueStats,
  queueLength,
  models,
  currentModel,
  onModelChange,
  controllerRef,
  versionFieldKey = "model",
  versionValue,
  versionLabel,
  versionOptions,
  onVersionChange,
  schemaSignature,
  position = 'sticky',
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const { catalog } = useModelStore();
  const hasModels = Array.isArray(models) && models.length > 0;
  const resolvedModel = currentModel ?? (hasModels ? models[0]?.value : undefined);
  const [schemaValueVersion, setSchemaValueVersion] = useState(0);

  useEffect(() => {
    const controller = controllerRef?.current;
    if (!controller || typeof controller.subscribeValueChanges !== "function") {
      return;
    }
    const unsubscribe = controller.subscribeValueChanges(() => {
      setSchemaValueVersion((prev) => prev + 1);
    });
    return () => {
      try {
        unsubscribe?.();
      } catch {
        // ignore
      }
    };
  }, [controllerRef, schemaSignature]);

  const handleModelSelect = (next: string) => {
    onModelChange?.(next);
  };

  const handleSubmit = () => {
    if (typeof onSubmit === "function") onSubmit();
  };

  // 校验主 Prompt 字段是否必填且为空时禁用提交
  const [promptFieldKey, setPromptFieldKey] = useState<string | null>(null);
  const [isPromptEmpty, setIsPromptEmpty] = useState<boolean>(false);
  const [promptIsRequired, setPromptIsRequired] = useState<boolean>(false);

  useEffect(() => {
    const ctrl = controllerRef?.current as any;
    try {
      const schema = typeof ctrl?.getSchema === "function" ? ctrl.getSchema() : null;
      const key = schema?.prompt?.primaryField;
      setPromptFieldKey(typeof key === "string" && key ? key : null);
    } catch {
      setPromptFieldKey(null);
    }
  }, [controllerRef, resolvedModel, schemaSignature]);

  useEffect(() => {
    if (!promptFieldKey) {
      setIsPromptEmpty(false);
      setPromptIsRequired(false);
      return;
    }

    const ctrl = controllerRef?.current as any;
    let disposed = false;
    let computeRunId = 0;
    const readValue = () => {
      try {
        const v = typeof ctrl?.getFieldValue === "function" ? ctrl.getFieldValue(promptFieldKey) : undefined;
        if (typeof v === "string") return v.trim();
        return v ? String(v) : "";
      } catch {
        return "";
      }
    };

    const readSchema = (): SchemaFormDefinition | null => {
      try {
        const s = typeof ctrl?.getSchema === "function" ? (ctrl.getSchema() as SchemaFormDefinition | null) : null;
        return s ?? null;
      } catch {
        return null;
      }
    };

    const buildValues = (schema: SchemaFormDefinition | null): SchemaFormValues => {
      const vals: Record<string, unknown> = {};
      try {
        const fields: any[] = Array.isArray(schema?.fields) ? (schema!.fields as any[]) : [];
        for (const f of fields) {
          const key = (f as any)?.key;
          if (typeof key === "string" && key) {
            vals[key] = typeof ctrl?.getFieldValue === "function" ? ctrl.getFieldValue(key) : undefined;
          }
        }
      } catch {
        // ignore partial
      }
      return vals as SchemaFormValues;
    };

    const computePromptRequired = async () => {
      const runId = ++computeRunId;
      const commit = (next: boolean) => {
        if (disposed || runId !== computeRunId) return;
        setPromptIsRequired(next);
      };

      const schema = readSchema();
      if (!schema) return commit(false);
      const field = (schema.fields as SchemaFieldDefinition[]).find((f) => (f as any)?.key === promptFieldKey) as any;
      if (!field) return commit(false);
      if (field.required === false) {
        commit(false);
        return;
      }
      // 优化：对永远必填的字段（required === true 且无 requiredWhen）直接常量化，避免后续重复计算。
      if (field.required === true && !field.requiredWhen) {
        commit(true);
        return;
      }
      const cond: SchemaCondition | undefined = field.requiredWhen;
      if (!cond) {
        commit(Boolean(field.required));
        return;
      }
      try {
        const values = buildValues(schema);
        const required = await evaluateCondition(cond, {
          field: promptFieldKey,
          schema: schema as any,
          values: values as any,
          modelKey: schema.meta?.modelKey ?? (currentModel ?? ""),
        });
        if (disposed || runId !== computeRunId) return;
        setPromptIsRequired(Boolean(required));
      } catch {
        if (disposed || runId !== computeRunId) return;
        setPromptIsRequired(false);
      }
    };

    // 初次挂载或依赖变化时基于当前值做一次同步校验
    setIsPromptEmpty(readValue() === "");
    void computePromptRequired();

    const container = typeof document !== "undefined"
      ? (document.querySelector(`[data-field-key="${promptFieldKey}"]`) as HTMLElement | null)
      : null;

    let inputEl: HTMLInputElement | HTMLTextAreaElement | null = null;
    const onInput = () => setIsPromptEmpty(readValue() === "");
    const bindInput = () => {
      if (!container) return;
      inputEl = (container.querySelector("textarea, input[type='text'], input[type='search']") as HTMLTextAreaElement | HTMLInputElement | null);
      if (inputEl) {
        inputEl.addEventListener("input", onInput);
        setIsPromptEmpty(readValue() === "");
        void computePromptRequired();
      }
    };

    let observer: MutationObserver | null = null;
    if (container && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        if (!inputEl || !container.contains(inputEl)) {
          if (inputEl) inputEl.removeEventListener("input", onInput);
          bindInput();
        }
      });
      observer.observe(container, { childList: true, subtree: true });
    }

    bindInput();

    // 移除 1s 轮询，改为依赖 subscribeValueChanges 驱动的 schemaValueVersion 精准触发。
    // 每当 SchemaFormRenderer 内部值发生变化时，controller.subscribeValueChanges 会更新 schemaValueVersion，
    // 进而重新执行当前 effect，完成 isPromptEmpty/promptIsRequired 的同步。

    return () => {
      if (inputEl) inputEl.removeEventListener("input", onInput);
      if (observer) observer.disconnect();
      disposed = true;
    };
  }, [controllerRef, promptFieldKey, resolvedModel, schemaSignature, currentModel, schemaValueVersion]);

  const submitDisabled =
    typeof onSubmit !== "function" ||
    (Boolean(promptFieldKey) && promptIsRequired && isPromptEmpty);

  const submitButtonLabel = useMemo(() => {
    const pending = queueStats ? queueStats.queued + queueStats.running : queueLength ?? 0;
    if (pending > 0) {
      return t("pages:create.promptDock.generatingWithQueue", {
        count: pending,
        defaultValue: `开始生成 (${pending} 处理中)`,
      }) as string;
    }
    return t("pages:create.promptDock.generate", { defaultValue: "开始生成" }) as string;
  }, [queueLength, queueStats, t]);

  // 顶部区：抽取 Prompt + 图片上传
  const [imageFields, setImageFields] = useState<Array<SchemaDataFieldDefinition<string>>>([]);
  const [visibleImageKeys, setVisibleImageKeys] = useState<Set<string>>(new Set());
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [doubaoLiteI2vImageMode, setDoubaoLiteI2vImageMode] = useState<"reference" | "firstFrame" | null>(null);
  const fileChangeHandlersRef = useRef<Map<string, (files: UploadFileItem[]) => void>>(new Map());

  const noopFilesChange = useCallback(() => {}, []);

  const handleFilesChange = useCallback(
    (fieldKey: SchemaDataFieldDefinition<string>["key"], next: UploadFileItem[]) => {
      const key = typeof fieldKey === "string" ? fieldKey : String(fieldKey ?? "");
      if (!key) return;
      try {
        const ctrl = controllerRef?.current;
        ctrl?.setFieldValue?.(key, next);
      } catch {
        // ignore
      }
    },
    [controllerRef]
  );

  const getFileChangeHandler = useCallback(
    (fieldKey: SchemaDataFieldDefinition<string>["key"]) => {
      const key = typeof fieldKey === "string" ? fieldKey : String(fieldKey ?? "");
      if (!key) return noopFilesChange;
      const cached = fileChangeHandlersRef.current.get(key);
      if (cached) return cached;
      const handler = (next: UploadFileItem[]) => handleFilesChange(key, next);
      fileChangeHandlersRef.current.set(key, handler);
      return handler;
    },
    [handleFilesChange, noopFilesChange]
  );

  useEffect(() => {
    fileChangeHandlersRef.current.clear();
  }, [handleFilesChange]);

  const readSchemaSafe = (): SchemaFormDefinition | null => {
    try {
      const ctrl = controllerRef?.current as any;
      const s = typeof ctrl?.getSchema === "function" ? (ctrl.getSchema() as SchemaFormDefinition | null) : null;
      return s ?? null;
    } catch {
      return null;
    }
  };

  const readAllValues = (schema: SchemaFormDefinition | null): SchemaFormValues => {
    const vals: Record<string, unknown> = {};
    try {
      const ctrl = controllerRef?.current as any;
      const fields: any[] = Array.isArray(schema?.fields) ? (schema!.fields as any[]) : [];
      for (const f of fields) {
        const key = (f as any)?.key;
        if (typeof key === "string" && key) {
          vals[key] = typeof ctrl?.getFieldValue === "function" ? ctrl.getFieldValue(key) : undefined;
        }
      }
    } catch {
      // ignore
    }
    return vals as SchemaFormValues;
  };

  useEffect(() => {
    const schema = readSchemaSafe();
    if (!schema) {
      setImageFields([]);
      setVisibleImageKeys(new Set());
      setDoubaoLiteI2vImageMode(null);
      return;
    }
    const imgs = (schema.fields as SchemaFieldDefinition[]).filter((f) =>
      (f as any)?.type === "imageUpload"
    ) as SchemaDataFieldDefinition<string>[];
    setImageFields(imgs);
    // 同步豆包 Seedance lite i2v 当前素材模式（参考图 / 首帧）
    try {
      const vals = readAllValues(schema) as any;
      const modelValue = vals?.model as string | undefined;
      const isDoubaoSeedanceSchema = schema.meta?.modelKey === "doubao-seedance";
      const isLiteI2vModel = isDoubaoSeedanceSchema && modelValue === "doubao-seedance-1-0-lite-i2v-250428";
      if (!isLiteI2vModel) {
        setDoubaoLiteI2vImageMode(null);
      } else {
        const firstItems = Array.isArray(vals?.firstFrameImage) ? (vals.firstFrameImage as UploadFileItem[]) : [];
        const referenceItems = Array.isArray(vals?.referenceImages) ? (vals.referenceImages as UploadFileItem[]) : [];
        if (referenceItems.length > 0 && firstItems.length === 0) {
          setDoubaoLiteI2vImageMode("reference");
        } else if (firstItems.length > 0 && referenceItems.length === 0) {
          setDoubaoLiteI2vImageMode("firstFrame");
        } else {
          // 默认回落到“参考图模式”，避免出现同时启用两类素材
          setDoubaoLiteI2vImageMode("reference");
        }
      }
    } catch {
      // ignore sync errors
      setDoubaoLiteI2vImageMode(null);
    }
  }, [controllerRef, schemaSignature]);

  useEffect(() => {
    let cancelled = false;
    const schema = readSchemaSafe();
    if (!schema) {
      setVisibleImageKeys(new Set());
      return;
    }
    const compute = async () => {
      const vals = readAllValues(schema);
      const next = new Set<string>();
      for (const f of imageFields) {
        try {
          const visible = await evaluateCondition(f.visibleWhen, {
            field: f.key,
            schema: schema as any,
            values: vals as any,
            modelKey: schema.meta?.modelKey ?? (currentModel ?? ""),
          });
          if (visible) next.add(f.key as string);
        } catch {
          next.add(f.key as string);
        }
      }
      if (!cancelled) setVisibleImageKeys(next);
    };
    void compute();
    return () => {
      cancelled = true;
    };
  }, [imageFields, controllerRef, currentModel, schemaSignature, schemaValueVersion]);

  const TopPromptAndImages = useMemo(() => {
    const schema = readSchemaSafe();
    if (!schema) return null;
    const promptKey = (schema.prompt?.primaryField ?? null) as string | null;
    if (!promptKey) return null;
    const ctrl = controllerRef?.current as any;
    const promptValue = typeof ctrl?.getFieldValue === "function"
      ? (ctrl.getFieldValue(promptKey) as unknown)
      : "";

    const allValues = readAllValues(schema) as any;
    const modelValue = allValues?.model as string | undefined;
    const isDoubaoSeedanceSchema = schema.meta?.modelKey === "doubao-seedance";
    const isLiteI2vModel =
      isDoubaoSeedanceSchema && modelValue === "doubao-seedance-1-0-lite-i2v-250428";
    const effectiveLiteMode: "reference" | "firstFrame" | null =
      isLiteI2vModel ? (doubaoLiteI2vImageMode ?? "reference") : null;

    let visibleUploads = imageFields.filter((f) => visibleImageKeys.has(f.key as string));

    // 针对 doubao-seedance-1-0-lite-i2v-250428：
    // 在 Prompt Dock 的图片弹窗中仅展示“参考图”或“首帧”其中一类上传入口，保证两者互斥。
    if (isLiteI2vModel && effectiveLiteMode) {
      visibleUploads = visibleUploads.filter((f) => {
        const key = String(f.key);
        if (effectiveLiteMode === "reference") {
          return key === "referenceImages";
        }
        if (effectiveLiteMode === "firstFrame") {
          return key === "firstFrameImage";
        }
        return true;
      });
    }

    const hasVisibleUploads = visibleUploads.length > 0;

    const handlePromptChange = (v: string) => {
      try { ctrl?.setFieldValue?.(promptKey!, v); } catch { /* ignore */ }
    };

    const handleLiteI2vModeChange = (next: "reference" | "firstFrame") => {
      if (!isLiteI2vModel) return;
      setDoubaoLiteI2vImageMode(next);
      try {
        if (next === "reference") {
          // 参考图模式：清空首帧 / 尾帧，仅保留参考图
          ctrl?.setFieldValue?.("firstFrameImage", []);
          ctrl?.setFieldValue?.("lastFrameImage", []);
        } else if (next === "firstFrame") {
          // 首帧模式：清空参考图 / 尾帧，仅保留首帧
          ctrl?.setFieldValue?.("referenceImages", []);
          ctrl?.setFieldValue?.("lastFrameImage", []);
        }
      } catch {
        // ignore
      }
    };

    return (
      <div
        className="grid gap-2 items-start"
        style={{
          gridTemplateColumns: hasVisibleUploads
            ? "minmax(0, 1fr) auto"
            : "minmax(0, 1fr)",
        }}
      >
        <div
          data-field-key={promptKey}
        >
          <Textarea
            value={typeof promptValue === "string" ? promptValue : String(promptValue ?? "")}
            className="border-0 shadow-none focus-visible:ring-0 focus-visible:border-0 bg-transparent p-0"
            onChange={(e) => handlePromptChange(e.target.value)}
            rows={6}
            placeholder="在此处输入想要生成的内容..."
          />
        </div>
        {hasVisibleUploads && (
          <div className="flex justify-end">
            <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="size-16"
                      aria-label={t("pages:create.promptDock.imageUploadButton", { defaultValue: "上传图片" }) as string}
                    >
                      <ImagePlus className="size-8" />
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  {t("pages:create.promptDock.imageUploadButton", { defaultValue: "上传图片" }) as string}
                </TooltipContent>
              </Tooltip>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <div className="flex items-center justify-between gap-2">
                    <DialogTitle>
                      {t("pages:create.promptDock.imageUploadTitle", { defaultValue: "图片上传" }) as string}
                    </DialogTitle>
                    {isLiteI2vModel && effectiveLiteMode && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="outline" size="sm">
                            {t("pages:create.promptDock.doubaoSeedance.liteMode.label", {
                              defaultValue: "素材模式",
                            }) as string}
                            ：
                            {effectiveLiteMode === "reference"
                              ? (t("pages:create.promptDock.doubaoSeedance.liteMode.reference", {
                                  defaultValue: "参考图模式",
                                }) as string)
                              : (t("pages:create.promptDock.doubaoSeedance.liteMode.firstFrame", {
                                  defaultValue: "首帧模式",
                                }) as string)}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuRadioGroup
                            value={effectiveLiteMode}
                            onValueChange={(value) =>
                              handleLiteI2vModeChange(
                                (value as "reference" | "firstFrame") || "reference"
                              )
                            }
                          >
                            <DropdownMenuRadioItem value="reference">
                              {t("pages:create.promptDock.doubaoSeedance.liteMode.reference", {
                                defaultValue: "参考图模式",
                              }) as string}
                            </DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="firstFrame">
                              {t("pages:create.promptDock.doubaoSeedance.liteMode.firstFrame", {
                                defaultValue: "首帧模式",
                              }) as string}
                            </DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </DialogHeader>
                <div className="mt-2 space-y-3 max-h-[60vh] overflow-y-auto">
                  {visibleUploads.map((field, idx) => {
                    const items = (() => {
                      try {
                        const v = ctrl?.getFieldValue?.(field.key as string);
                        return Array.isArray(v) ? (v as UploadFileItem[]) : [];
                      } catch { return [] as UploadFileItem[]; }
                    })();
                    const effectiveModelKey = modelValue ?? schema.meta?.modelKey ?? currentModel ?? "";
                    const maxFiles = resolveUploadMaxFiles(
                      field as SchemaDataFieldDefinition<string>,
                      allValues as SchemaFormValues<string>,
                      effectiveModelKey
                    );
                    const accept = (field as any).accept ?? "image/*";
                    const labelKey = (field as any).labelKey as string | undefined;
                    const fieldKey = typeof field?.key === "string" ? field.key : String(field?.key ?? "");
                    const fileChangeHandler = getFileChangeHandler(fieldKey);
                    return (
                      <div
                        key={`${fieldKey}.${idx}`}
                        data-field-key={fieldKey}
                        className="space-y-2"
                      >
                        {labelKey ? (
                          <div className="flex items-center mb-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label={t(labelKey)}
                                >
                                  <ImagePlus className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t(labelKey)}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        ) : null}
                        <FileUpload
                          value={items}
                          onFilesChange={fileChangeHandler}
                          accept={accept}
                          maxSizeMB={(field as any).maxSizeMB}
                          maxDimension={(field as any).maxDimension}
                          imageResizeMode={(field as any).imageResizeMode}
                          imageConstraints={(field as any).imageConstraints}
                          maxFiles={maxFiles}
                          multiple={(maxFiles ?? 1) > 1}
                        />
                      </div>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    imageFields,
    visibleImageKeys,
    controllerRef,
    schemaSignature,
    t,
    imageDialogOpen,
    doubaoLiteI2vImageMode,
    schemaValueVersion,
    getFileChangeHandler,
  ]);

  // 版本选择（聚合所有模型的 Schema 选项，并在命令面板中按模型分组展示）
  type GroupedVersionMap = Record<string, { fieldLabel: string; options: VersionOption[] }>;
  type ModelWithVersionOptions = ModelItem & {
    versionLabel?: string;
  };
  const defaultVersionLabel = useMemo(
    () => t("pages:modelWorkspace.veo.fields.model.label", { defaultValue: "模型版本" }) as string,
    [t]
  );
  const resolvedVersionLabel = versionLabel ?? defaultVersionLabel;
  const hasCurrentVersionOptions = Boolean(
    resolvedModel && Array.isArray(versionOptions) && versionOptions.length > 0
  );
  const groupedVersionOptions = useMemo<GroupedVersionMap>(() => {
    const map: GroupedVersionMap = {};
    (models as ModelWithVersionOptions[]).forEach((model) => {
      const opts = Array.isArray(model.versions)
        ? model.versions.filter((v) => v.visible !== false)
        : [];
      if (!opts.length) return;
      const label =
        typeof model.versionLabel === "string" && model.versionLabel.trim().length > 0
          ? model.versionLabel
          : defaultVersionLabel;
      map[model.value] = {
        fieldLabel: label,
        options: opts,
      };
    });
    if (resolvedModel) {
      const existing = map[resolvedModel];
      if ((!existing || existing.options.length === 0) && hasCurrentVersionOptions && Array.isArray(versionOptions)) {
        map[resolvedModel] = {
          fieldLabel: resolvedVersionLabel,
          options: versionOptions.filter((v) => v.visible !== false),
        };
      }
    }
    return map;
  }, [
    models,
    resolvedModel,
    versionOptions,
    resolvedVersionLabel,
    defaultVersionLabel,
    hasCurrentVersionOptions,
  ]);
  const currentVersionOptions =
    (resolvedModel && groupedVersionOptions[resolvedModel]?.options) || [];
  const resolvedVersionValue =
    typeof versionValue === "string"
      ? versionValue
      : currentVersionOptions[0]?.value ?? "";
  const hasVersionSelector = Boolean(resolvedModel && currentVersionOptions.length > 0);

  // 模型选择对话框开关
  const [modelDialogOpen, setModelDialogOpen] = useState(false);



  // 观测并写入 Dock 实际高度
  // - fixed/sticky：写入到 documentElement 的 --prompt-dock-height，供 PageContainer 预留底部空间
  // - floating：仅本地使用高度，避免 PageContainer 产生全局 paddingBottom
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [dockHeight, setDockHeight] = useState<number>(0);
  useEffect(() => {
    const el = panelRef.current || rootRef.current;
    if (!el || typeof window === "undefined") return;
    const parentForFloating = rootRef.current?.parentElement as HTMLElement | null;

    const applyHeight = () => {
      const h = el.offsetHeight || 0;
      setDockHeight(h);
      if (position === 'floating') {
        // 浮动形态：仅在本地父容器设置变量，避免 PageContainer 额外 padding
        if (parentForFloating) {
          try {
            parentForFloating.style.setProperty("--prompt-dock-height", `${h}px`);
          } catch {
            // ignore
          }
        }
        try {
          document.documentElement.style.removeProperty("--prompt-dock-height");
        } catch { /* ignore */ }
      } else {
        try {
          document.documentElement.style.setProperty("--prompt-dock-height", `${h}px`);
        } catch {
          // ignore
        }
      }
    };
    applyHeight();
    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(() => applyHeight());
      ro.observe(el);
    } catch {
      // ignore
    }
    const onResize = () => applyHeight();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
      if (position === 'floating') {
        if (parentForFloating) {
          try {
            parentForFloating.style.removeProperty("--prompt-dock-height");
          } catch {
            // ignore
          }
        }
      } else {
        try {
          document.documentElement.style.removeProperty("--prompt-dock-height");
        } catch { /* ignore */ }
      }
    };
  }, [schemaSignature, resolvedModel, position]);

  // 新内部结构：
  // - 上区：仅 Prompt + 图片上传
  // - 下区：参数设置（模型/版本/生成 + 其余表单参数）
  const inner = (
    <div className="flex flex-col gap-3 md:gap-4">
      {/* 上区：Prompt + 图片上传 */}
      <div className="w-full min-w-[220px] max-h-[320px] overflow-y-auto">
        {TopPromptAndImages}
      </div>

      {/* 下区：参数设置 */}
      <div className="flex items-center justify-between">
        {/* 顶部工具行：模型/版本/生成 */}
        <div className="flex items-center gap-2 flex-1 flex-wrap">
        {hasModels ? (
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="hidden sm:inline-flex text-muted-foreground"
                  aria-label={t("pages:create.promptDock.modelLabel", { defaultValue: "当前模型" })}
                >
                  <SlidersHorizontal className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("pages:create.promptDock.modelLabel", { defaultValue: "当前模型" })}
              </TooltipContent>
            </Tooltip>
            {/* 模型/版本命令面板触发按钮 */}
            <Button
              type="button"
              variant="outline"
              size="default"
              className="min-w-[160px] justify-between"
              disabled={!hasModels}
              onClick={() => setModelDialogOpen(true)}
            >
              <span className="inline-flex items-center gap-1">
                {(() => {
                  const currentModelData = models.find(m => m.value === resolvedModel);
                  const groupId = currentModelData?.groupId;
                  const groupIcon = catalog?.groups.find(g => g.id === groupId)?.icon;
                  return groupIcon ? (
                    <ModelSelectorLogoGroup className="-ml-0">
                      <img src={groupIcon} alt="" className="size-3" />
                    </ModelSelectorLogoGroup>
                  ) : null;
                })()}
                <span>
                  {(() => {
                    const m = models.find((x) => x.value === resolvedModel);
                    const versionLabelResolved =
                      currentVersionOptions.find((o) => o.value === resolvedVersionValue)?.label ??
                      (hasVersionSelector ? resolvedVersionValue : undefined) ??
                      resolvedModel;
                    if (m) {
                      return versionLabelResolved || m.label;
                    }
                    return resolvedModel ?? '';
                  })()}
                </span>
              </span>
            </Button>
            {/* 模型命令面板 */}
            <ModelSelectorDialog open={modelDialogOpen} onOpenChange={setModelDialogOpen}>
              <ModelSelectorInput
                placeholder={
                  (t("pages:create.promptDock.modelSearch", { defaultValue: "搜索模型或版本..." }) as string)
                }
              />
              <ModelSelectorList>
                <ModelSelectorEmpty>
                  {t("common:empty.search", { defaultValue: "未找到匹配项" }) as string}
                </ModelSelectorEmpty>
                {(() => {
                  if (!catalog) return null;

                  return catalog.groups
                    .filter(group => !group.hidden)
                    .map((group) => {
                      const groupModels = catalog.modelsByGroup[group.id] ?? [];
                      if (groupModels.length === 0) return null;

                      return (
                        <ModelSelectorGroup key={group.id} heading={group.label}>
                          {groupModels.map(model => {
                            const versionOpts = groupedVersionOptions[model.value]?.options ?? [];
                            return versionOpts.length > 0 ? (
                              versionOpts.map(opt => (
                                <ModelSelectorItem
                                  key={`${model.value}::${opt.value}`}
                                  value={`${model.value}::${opt.value}`}
                                  onSelect={() => {
                                    const isSameModel = model.value === resolvedModel;
                                    if (!isSameModel) {
                                      handleModelSelect(model.value);
                                    }
                                    onVersionChange?.(opt.value);
                                    setModelDialogOpen(false);
                                  }}
                                >
                                  {group.icon ? (
                                    <ModelSelectorLogoGroup>
                                      <img src={group.icon} alt="" className="size-3" />
                                    </ModelSelectorLogoGroup>
                                  ) : null}
                                  <ModelSelectorName>{opt.label}</ModelSelectorName>
                                </ModelSelectorItem>
                              ))
                            ) : (
                              <ModelSelectorItem
                                key={model.value}
                                value={model.value}
                                onSelect={() => {
                                  if (model.value !== resolvedModel) {
                                    handleModelSelect(model.value);
                                  }
                                  setModelDialogOpen(false);
                                }}
                              >
                                {group.icon ? (
                                  <ModelSelectorLogoGroup>
                                    <img src={group.icon} alt="" className="size-3" />
                                  </ModelSelectorLogoGroup>
                                ) : null}
                                <ModelSelectorName>{model.label}</ModelSelectorName>
                              </ModelSelectorItem>
                            );
                          })}
                        </ModelSelectorGroup>
                      );
                    });
                })()}
              </ModelSelectorList>
            </ModelSelectorDialog>
          </div>
        ) : (
          <span />
        )}

          <div className="flex items-center gap-2 flex-wrap flex-1">
            {content}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitDisabled}
              aria-busy={submitting}
              className="flex-shrink-0"
            >
              <Play className="mr-1.5" />
              {submitButtonLabel}
            </Button>
          {queueStats && queueStats.total > 0 && (
            <TaskProgressBar stats={queueStats} className="mt-1 w-full min-w-[180px]" />
          )}
        </div>
        </div>
      </div>
  );

  if (position === 'floating') {
    // 容器内 overlay：不占用布局高度，宽度随容器；额外渲染一个占位块防止遮挡
    return (
      <>
        <div
          ref={rootRef}
          data-immersive-translate-dont-translate
          translate="no"
          className="pointer-events-none absolute left-0 right-0 bottom-2 z-50"
        >
        <div
          ref={panelRef}
          className="pointer-events-auto w-full max-w-[min(1400px,100%)] mx-auto rounded-xl p-2 md:p-3 border border-primary/10 bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/70"
        >
          {inner}
        </div>
        </div>
        {/* 占位：让底部内容不被浮动 Dock 遮挡（仅浮动模式生效） */}
        <div aria-hidden className="w-full shrink-0" style={{ height: dockHeight || 0 }} />
      </>
    );
  }

  return (
    <div
      ref={rootRef}
      data-immersive-translate-dont-translate
      className={
        position === 'fixed'
          ? "notranslate fixed inset-x-0 bottom-0 z-50 px-4 md:px-6 lg:px-24 py-3 md:py-4 pointer-events-none"
          : "notranslate sticky bottom-0 z-50 px-4 md:px-6 lg:px-24 py-3 md:py-4 pointer-events-none"
      }
      translate="no"
    >
      <div className="w-full flex justify-center">
        <div
          ref={panelRef}
          className={
            position === 'fixed'
              ? "pointer-events-auto w-full max-w-[min(1400px,90vw)] rounded-xl p-2 md:p-3 border border-primary/10 bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/70"
              : "pointer-events-auto w-full max-w-[min(1400px,100%)] rounded-xl p-2 md:p-3 border border-primary/10 bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/70"
          }
        >
          {inner}
        </div>
      </div>
    </div>
  );
};

export default PromptDock;
